import type { Atlas } from '@/models/interfaces/Atlas'
import type { AtlasMap } from '@/models/interfaces/map/AtlasMap'
import type { AtlasLayer } from '@/models/interfaces/map/Layer'
import type { AtlasStoreType, MyMapStoreType } from '@/models/interfaces/Stores'

export class AtlasMapService {
  static mapStore: MyMapStoreType | null = null
  static atlasStore: AtlasStoreType | null = null

  static async initAtlasLayers(
    mapStore: MyMapStoreType,
    atlasStore: AtlasStoreType,
  ): Promise<void> {
    this.mapStore = mapStore
    this.atlasStore = atlasStore
    await this.atlasStore.getAll()
    for (const atlas of this.atlasStore.atlasList) {
      const atlasLayers = await this.setAtlasLayers(atlas)
      this.mapStore.atlasMaps.push(...atlasLayers)
    }
  }

  static async setAtlasLayers(atlas: Atlas): Promise<AtlasMap[]> {
    const atlasMaps: AtlasMap[] = []

    for (const map of atlas.maps) {
      let isMainLayerShown = false
      const activeSubLayers: string[] = []

      if (this.mapStore?.deserializedMapState) {
        isMainLayerShown = !!this.mapStore.deserializedMapState.layers.atlasMaps?.some(
          (atlasMap) => atlasMap.id === map['@id'],
        )
        if (isMainLayerShown) {
          const atlasMap = this.mapStore.deserializedMapState.layers.atlasMaps?.find(
            (atlasMap) => atlasMap.id === map['@id'],
          )
          if (atlasMap) {
            activeSubLayers.push(...atlasMap.subLayers)
          }
        }
      }

      const mainLayer: AtlasLayer = {
        id: map['@id'],
        name: map.name,
        isShown: isMainLayerShown,
        icon: map.logo?.contentUrl || '',
        opacity: 100,
        atlasGroup: atlas.atlasGroup,
      }

      // Initialisation of sublayers with a placeholder for icons
      // QGIS functionality removed - sublayers initialization disabled
      const subLayers: AtlasLayer[] = []

      atlasMaps.push({
        id: map['@id'],
        mainLayer: mainLayer,
        subLayers: subLayers,
        qgisProjectName: map.qgisProject?.name || '',
        atlasId: atlas['@id'],
        needsToBeVisualiseAsPlainImageInsteadOfWMS: map.needsToBeVisualiseAsPlainImageInsteadOfWMS,
      })

      // Loading sublayers icons in parallel
      ;(async () => {
        for (const subLayer of subLayers) {
          // QGIS functionality removed - icon loading disabled
          subLayer.icon = subLayer.icon
        }
      })()
    }
    return atlasMaps
  }

  static handleAtlasLayersVisibility(
    atlasMaps: AtlasMap[],
    map: maplibregl.Map | null | undefined,
    atlasMapId: string,
  ) {
    if (map) {
      for (const atlasMap of atlasMaps) {
        if (atlasMap.id !== atlasMapId) continue

        let keepSource = true
        const layersToAdd = []

        if (atlasMap.mainLayer.isShown || atlasMap.subLayers.some((layer) => layer.isShown)) {
          layersToAdd.push(
            ...atlasMap.subLayers.filter((layer) => layer.isShown).map((layer) => layer.name),
          )
        } else {
          if (atlasMap.needsToBeVisualiseAsPlainImageInsteadOfWMS) {
            keepSource = false
          }
        }

        if (!keepSource) {
          // QGIS functionality removed
          return
        }
        // QGIS functionality removed - layers cannot be added without QgisMapMaplibreService
      }
    }
  }
}
